import {
    CouponEntity,
    OrderEntity,
    TransactionEntity,
    UserEntity,
    VoucherEntity,
} from '../../domain/entities/userEntity';

/**
 * User Model
 * Maps API responses to domain entities
 */
export class UserModel {
    /**
     * Map API user data to UserEntity
     */
    static fromApi(apiData) {
        return new UserEntity({
            id: apiData.id || apiData.userId,
            name: apiData.name || apiData.fullName,
            firstName: apiData.firstName || apiData.first_name,
            lastName: apiData.lastName || apiData.last_name,
            phone: apiData.phone || apiData.phoneNumber,
            email: apiData.email,
            avatar: apiData.avatar || apiData.avatarUrl || apiData.profilePicture,
            memberStatus: apiData.memberStatus || apiData.member_status || 'Member',
            creditBalance: parseFloat(apiData.creditBalance || apiData.credit_balance || 0),
            points: parseInt(apiData.points || 0),
            title: apiData.title || apiData.jobTitle || '',
        });
    }

    /**
     * Map UserEntity to API format for updates
     */
    static toApi(userEntity) {
        return {
            firstName: userEntity.firstName,
            lastName: userEntity.lastName,
            email: userEntity.email,
            phone: userEntity.phone,
            title: userEntity.title,
        };
    }
}

/**
 * Transaction Model
 * Maps API transaction data to TransactionEntity
 */
export class TransactionModel {
    static fromApi(apiData) {
        return new TransactionEntity({
            id: apiData.id || apiData.transactionId,
            type: apiData.type || apiData.transactionType,
            amount: parseFloat(apiData.amount || 0),
            description: apiData.description || apiData.notes,
            date: apiData.date || apiData.createdAt || apiData.created_at,
            status: apiData.status || 'completed',
        });
    }

    static fromApiList(apiList) {
        return (apiList || []).map((item) => TransactionModel.fromApi(item));
    }
}

/**
 * Voucher Model
 * Maps API voucher data to VoucherEntity
 */
export class VoucherModel {
    static fromApi(apiData) {
        return new VoucherEntity({
            id: apiData.id || apiData.voucherId,
            code: apiData.code || apiData.voucherCode,
            name: apiData.name || apiData.voucherName,
            description: apiData.description,
            discount: apiData.discount || apiData.discountAmount,
            expiryDate: apiData.expiryDate || apiData.expiry_date || apiData.validUntil,
            status: apiData.status,
            imageUrl: apiData.imageUrl || apiData.image_url || apiData.image,
        });
    }

    static fromApiList(apiList) {
        return (apiList || []).map((item) => VoucherModel.fromApi(item));
    }
}

/**
 * Coupon Model
 * Maps API coupon data to CouponEntity
 */
export class CouponModel {
    static fromApi(apiData) {
        return new CouponEntity({
            id: apiData.id || apiData.couponId,
            code: apiData.code || apiData.couponCode,
            name: apiData.name || apiData.couponName,
            description: apiData.description,
            discount: apiData.discount || apiData.discountAmount,
            expiryDate: apiData.expiryDate || apiData.expiry_date || apiData.validUntil,
            status: apiData.status,
            imageUrl: apiData.imageUrl || apiData.image_url || apiData.image,
        });
    }

    static fromApiList(apiList) {
        return (apiList || []).map((item) => CouponModel.fromApi(item));
    }
}

/**
 * Order Model
 * Maps API order data to OrderEntity
 */
export class OrderModel {
    static fromApi(apiData) {
        return new OrderEntity({
            id: apiData.id || apiData.orderId,
            orderNumber: apiData.orderNumber || apiData.order_number || apiData.invoiceNumber,
            date: apiData.date || apiData.orderDate || apiData.createdAt,
            items: apiData.items || apiData.orderItems || [],
            total: parseFloat(apiData.total || apiData.totalAmount || 0),
            status: apiData.status || apiData.orderStatus,
            paymentMethod: apiData.paymentMethod || apiData.payment_method,
        });
    }

    static fromApiList(apiList) {
        return (apiList || []).map((item) => OrderModel.fromApi(item));
    }
}
